import {
  Grid2,
  Typography,
  Box,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CollapsibleSection } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list-collapsible-section';
import {
  JsmIssueBase,
  projectPortalMap,
} from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';

interface DetailsCollapseProps {
  issue: JsmIssueBase;
  expanded: boolean;
  toggleSection: () => void;
  portalKey: string; // e.g. "REALMS", "MCD", etc.
}

export function DetailsCollapse({
  issue,
  expanded,
  toggleSection,
  portalKey,
}: DetailsCollapseProps) {
  const descriptionStyle = { fontWeight: 500 };
  const bubbleStyle = {
    backgroundColor: '#f0f0f0',
    borderRadius: '8px',
    padding: '0 8px',
    display: 'inline-block',
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  const versionThreshold = isSmallScreen ? 2 : 5;
  const [showAllAffectsVersions, setShowAllAffectsVersions] = useState(false);
  const [showAllFixVersions, setShowAllFixVersions] = useState(false);

  const allowedCustomFields = projectPortalMap[portalKey]?.customFields ?? [];

  function canShow(fieldName: string) {
    return allowedCustomFields.includes(fieldName);
  }

  const versions = issue.fields.versions ?? [];
  const displayedVersions = showAllAffectsVersions
    ? versions
    : versions.slice(0, versionThreshold);

  const fixVersions = issue.fields.fixVersions ?? [];
  const displayedFixVersions = showAllFixVersions
    ? fixVersions
    : fixVersions.slice(0, versionThreshold);

  const pathParts = location.pathname.split('/');
  const basePath = '/' + pathParts.slice(1, 3).join('/');

  const handleLabelClick = (label: string) => {
    window.location.href = `${basePath}?label=${encodeURIComponent(label)}`;
  };

  const handleFixVersionClick = (fixVersion: string) => {
    window.location.href = `${basePath}?fixVersion=${encodeURIComponent(fixVersion)}`;
  };

  const renderFieldLine = (label: string, content?: string | null) => {
    if (!content) return null;
    return (
      <Typography sx={{ marginBottom: 2 }}>
        <Typography component="span" sx={descriptionStyle}>
          {label}:
        </Typography>{' '}
        <Box component="span" sx={bubbleStyle}>
          {content}
        </Box>
      </Typography>
    );
  };

  // Renders an array of objects with .value (example for Category)
  const renderArrayOfValues = (
    label: string,
    arr: Array<{ value: string }>
  ) => {
    if (!arr || arr.length === 0) return null;
    const joined = arr.map((item) => item.value).join(', ');
    return renderFieldLine(label, joined);
  };

  return (
    <CollapsibleSection
      title="Details"
      expanded={expanded}
      setExpanded={toggleSection}
    >
      <Grid2 container spacing={2}>
        {/* ---------------- LEFT COLUMN ---------------- */}
        <Grid2>
          {/* ---------- Type (Standard Field) ---------- */}
          <Typography
            sx={{
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography component="span" sx={descriptionStyle}>
              Type:
            </Typography>
            <Box
              component="span"
              sx={{
                ...bubbleStyle,
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: 1,
              }}
            >
              {issue.fields.issuetype.iconUrl && (
                <img
                  src={issue.fields.issuetype.iconUrl}
                  alt="Issue Icon"
                  style={{
                    width: 20,
                    height: 20,
                    marginLeft: 4,
                    marginRight: 4,
                  }}
                />
              )}
              {/* If no issuetype name, fallback to "None" */}
              {issue.fields.issuetype?.name || 'None'}
            </Box>
          </Typography>

          {/* ---------- Affects Version/s (Standard Field) ---------- */}
          <Typography sx={{ marginBottom: 2 }}>
            <Typography component="span" sx={descriptionStyle}>
              Affects Version/s:
            </Typography>{' '}
            <Box component="span" sx={bubbleStyle}>
              {versions.length === 0 ? (
                <>None</>
              ) : (
                <>
                  {displayedVersions.map((v) => v.name).join(', ')}
                  {versions.length > versionThreshold && (
                    <>
                      {' '}
                      {!showAllAffectsVersions ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => setShowAllAffectsVersions(true)}
                          sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          (...{versions.length - versionThreshold})
                        </Link>
                      ) : (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => setShowAllAffectsVersions(false)}
                          sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          ...
                        </Link>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Typography>

          {/* ---------- Labels (Standard Field) ---------- */}
          <Typography sx={{ marginBottom: 2 }}>
            <Typography component="span" sx={descriptionStyle}>
              Labels:
            </Typography>{' '}
            <Box component="span" sx={bubbleStyle}>
              {issue.fields.labels && issue.fields.labels.length > 0 ? (
                issue.fields.labels.map((label, index) => (
                  <Link
                    key={label}
                    component="button"
                    variant="body2"
                    onClick={() => handleLabelClick(label)}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      marginRight:
                        index < issue.fields.labels.length - 1 ? 1 : 0,
                    }}
                  >
                    {label}
                  </Link>
                ))
              ) : (
                <>None</>
              )}
            </Box>
          </Typography>

          {/* customfield_10054 => Confirmation Status */}
          {canShow('customfield_10054') &&
            renderFieldLine(
              'Confirmation Status',
              issue.fields.customfield_10054?.value
            )}

          {/* customfield_10056 => Realms Platform */}
          {canShow('customfield_10056') &&
            renderFieldLine(
              'Realms Platform',
              issue.fields.customfield_10056?.value
            )}

          {/* customfield_10050 => ADO (string) */}
          {canShow('customfield_10050') &&
            renderFieldLine('ADO', issue.fields.customfield_10050)}

          {/* customfield_10102 => Category (array) */}
          {canShow('customfield_10055') &&
            renderArrayOfValues('Category', issue.fields.customfield_10055)}

          {/* customfield_10051 => Area */}
          {canShow('customfield_10051') &&
            renderFieldLine('Area', issue.fields.customfield_10051?.value)}

          {/* customfield_10063 => Platform */}
          {canShow('customfield_10063') &&
            renderFieldLine('Platform', issue.fields.customfield_10063?.value)}

          {/* customfield_10062 => Dungeons Platform */}
          {canShow('customfield_10062') &&
            renderFieldLine(
              'Dungeons Platform',
              issue.fields.customfield_10062?.value
            )}

          {/* customfield_10048 => Game Mode */}
          {canShow('customfield_10048') &&
            renderFieldLine('Game Mode', issue.fields.customfield_10048?.value)}

          {/* customfield_10109 => Affected Platforms */}
          {canShow('customfield_10053') &&
            renderFieldLine(
              'Affected Platforms',
              issue.fields.customfield_10053?.value
            )}

          {/* customfield_10060 => Legends Platform */}
          {canShow('customfield_10060') &&
            renderFieldLine(
              'Legends Platform',
              issue.fields.customfield_10060?.value
            )}

          {/* customfield_10061 => Operating System Version */}
          {canShow('customfield_10061') &&
            renderFieldLine(
              'Operating System Version',
              issue.fields.customfield_10061?.value
            )}

          {/* customfield_10049 => Mojang Priority */}
          {canShow('customfield_10049') &&
            renderFieldLine(
              'Mojang Priority',
              issue.fields.customfield_10049?.value
            )}
        </Grid2>

        <Grid2>
          {/* ---------- Resolution (Standard Field) ---------- */}
          <Typography sx={{ marginBottom: 2 }}>
            <Typography component="span" sx={descriptionStyle}>
              Resolution:
            </Typography>{' '}
            <Box component="span" sx={bubbleStyle}>
              {issue.fields.resolution?.name || 'None'}
            </Box>
          </Typography>

          {/* ---------- Fix Version/s (Standard Field) ---------- */}
          <Typography sx={{ marginBottom: 2 }}>
            <Typography component="span" sx={descriptionStyle}>
              Fix Version/s:
            </Typography>{' '}
            <Box component="span" sx={bubbleStyle}>
              {fixVersions.length === 0 ? (
                <>None</>
              ) : (
                <>
                  {displayedFixVersions.map((fv, idx) => (
                    <span key={fv.name}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleFixVersionClick(fv.name)}
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        {fv.name}
                      </Link>
                      {idx < displayedFixVersions.length - 1 && ', '}
                    </span>
                  ))}

                  {fixVersions.length > versionThreshold && (
                    <>
                      {' '}
                      {!showAllFixVersions ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => setShowAllFixVersions(true)}
                          sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            marginLeft: 0.5,
                          }}
                        >
                          (...{fixVersions.length - versionThreshold})
                        </Link>
                      ) : (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => setShowAllFixVersions(false)}
                          sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            marginLeft: 0.5,
                          }}
                        >
                          ...
                        </Link>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Typography>
        </Grid2>
      </Grid2>
    </CollapsibleSection>
  );
}
